import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import _, { sortBy, uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  fetchTaskReportLabelGroupCategoryDetail,
  fetchTaskReportLabelGroupCategoryDetailMax,
  fetchTaskReportLabelGroupCategoryDetailSummary,
  fetchTaskReportLabelGroupCategoryDetailYearlySummary,
  fetchTaskSearch,
  resetTaskReportLabelGroupCategoryDetailSummary,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import { RequestGetTaskSearch, ResponseGetTaskReportLabelGroupCategoryDetailYearlySummary } from '../../../../states/types/task'
import {
  dateManipulateMonth,
  endOfMonth,
  format,
  nowDate,
  startOfMonth,
} from '../../../../utils/date'
import { getGetPrams, setPath } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import DetailCostChart from '../../../components/task/DetailCostChart'
import DetailScheduleChart from '../../../components/task/DetailScheduleChart'
import DetailRow from '../../../components/task/DetailRow'
import DateModule from '../../../components/task/MonthCalendarModule'
import { URL } from '../../../constants/url'
import ReportSummaryModule from '../../../components/task/ReportSummaryModule'
import DetailRowForCategory from '../../../components/task/DetailRowForCategory'
import PdfModule from '../../../components/common/PdfModule'
import { stringToHexSimple } from '../../../../utils/color'
import LineChart from '../../../components/task/LineChart'

interface GetParams {
  date: string
}

interface GraphData {
  date: string
  labeling: {
    label_groups: {
      id: number
      name: string
      color: string
      time: number
    }[]
    scheduled: number
  }
  accounting: {
    budget: number
    other_costs: {
      name: string
      cost: number
    }[]
    cost: number
  }
}

export interface LabelGroup {
  id: number
  name: string
  color: string
  time: {
    scheduled: number | null // 予定時間
    actual: number // 実際にかかった時間
  }
  cost: {
    scheduled: number | null // 予算
    actual: number // 実際にかかったコスト
    other: number // その他コスト
  }
  approved: boolean
}

export interface LabelGroupCategory {
  id: number
  name: string
  color: string
  time: {
    scheduled: number | null // 予定時間
    actual: number // 実際にかかった時間
  }
  cost: {
    scheduled: number | null // 予算
    actual: number // 実際にかかったコスト
    other: number // その他コスト
  }
  approved: boolean
  label_group_duplicated: boolean
  children: {
    label_group_categories: LabelGroupCategory[]
    label_groups: LabelGroup[]
  }
}

interface List {
  id: number
  name: string
  color: string
  time: {
    scheduled: number | null // 予定時間
    actual: number // 実際にかかった時間
  }
  cost: {
    scheduled: number | null // 予算
    actual: number // 実際にかかったコスト
    others: { // その他コスト
      name: string
      cost: number
    }[]
  }
  children: {
    label_group_categories: LabelGroupCategory[]
    label_groups: LabelGroup[]
  }
  labels: {
    id: number
    name: string
    time: number
    cost: number
    color: string
    label_groups: {
      id: number
      name: string
      time: number
      cost: number
      color: string
      approved: boolean
    }[]
    approved: boolean
  }[]
  label_codes: {
    code: string | null
    cost: number
    time: number
    labels: {
      id: number
      name: string
      code: string | null
      color: string
      cost: number
      time: number
      approved: boolean
    }[]
    approved: boolean
  }[]
  members: {
    id: number
    name: string | null
    profile_image: string | null
    cost: number
    time: number
    label_groups: {
      id: number
      name: string
      time: number
      cost: number
      color: string
      approved: boolean
    }[]
    approved: boolean
  }[]
}

const Detail: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const dispatch = useDispatch()

  const {
    task,
    login,
    commonPart,
  } = useSelector((state: RootState) => state)

  const DISPLAY_UNDER_LIMIT = 6

  const DISPLAY_GRAPH_LIMIT = 4

  const DISPLAY_TASK_DETAIL_LIMIT_MINUTES = 1

  const DISPLAY_MEMBER_LIMIT = 30
  const DISPLAY_PROJECT_LIMIT = 30

  const [date, setDate] = useState<string>("")
  const [selectedLabelGroupCategory, setSelectedLabelGroupCategory] = useState<List>()
  const [isDisplayTime, setIsDisplayTime] = useState<boolean>(true)

  const [isFirst, setIsFirst] = useState<boolean>(true)

  const [selectLabel, setSelectLabel] = useState<string>("")

  const [graphData, setGraphData] = useState<GraphData[]>([])

  const [graphDisplayRange, setGraphDisplayRange] = useState<{ startDate: string, endDate: string }>({ startDate: "", endDate: "" })

  const [isGraphLoading, setIsGraphLoading] = useState<boolean>(true)

  const [isDisplayCategoryDetail, setIsDisplayCategoryDetail] = useState<boolean>(false)
  const [isDisplayLabelDetail, setIsDisplayLabelDetail] = useState<boolean>(false)
  const [isDisplayMemberDetail, setIsDisplayMemberDetail] = useState<boolean>(false)

  const [sortLabel, setSortLabel] = useState<string>("")

  const [displayType, setDisplayType] = useState<string>("all")

  const [isDisplayLabelCodeGroup, setIsDisplayLabelCodeGroup] = useState<boolean>(false)

  const [displayRangeType, setDisplayRangeType] = useState<string>("all")

  const [year, setYear] = useState<number>(Number(nowDate("YYYY")))
  const [selectYearMemberIds, setSelectYearMemberIds] = useState<number[]>([])
  const [selectYearLabelGroupIds, setSelectYearLabelGroupIds] = useState<number[]>([])

  const [yearData, setYearData] = useState<ResponseGetTaskReportLabelGroupCategoryDetailYearlySummary>({ list: [], members: [], label_groups: [] })
  const [isDisplayCategoryTime, setIsDisplayCategoryTime] = useState<boolean>(true)
  const [isDisplayWorkTime, setIsDisplayWorkTime] = useState<boolean>(true)
  const [isDisplayWorkCodeTime, setIsDisplayWorkCodeTime] = useState<boolean>(true)
  const [isDisplayMemberTime, setIsDisplayMemberTime] = useState<boolean>(true)
  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const [displayMemberPage, setDisplayMemberPage] = useState<number>(1)
  const [displayProjectPage, setDisplayProjectPage] = useState<number>(1)

  const endDate = useMemo(() => {
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return nowDate("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const profit = useMemo(() => {
    if (!selectedLabelGroupCategory) return 0
    return (selectedLabelGroupCategory.cost.scheduled || 0)
    - (selectedLabelGroupCategory.cost.others.reduce((s, v) => s + v.cost, 0) + selectedLabelGroupCategory.cost.actual)
  }, [selectedLabelGroupCategory])

  const graphLimit = useMemo(() => {
    if (!selectedLabelGroupCategory) return 0
    if (profit > 0) {
      return selectedLabelGroupCategory.cost.scheduled || 0
    }
    return (selectedLabelGroupCategory.cost.others.reduce((s, v) => s + v.cost, 0) + selectedLabelGroupCategory.cost.actual)
  }, [profit])

  const rangeTypeTitle = useMemo(() => {
    if (displayRangeType === "all") {
      return "全期間"
    }
    if (displayRangeType === "year") {
      return "年間"
    }
    if (displayRangeType === "month") {
      return format(date, "YYYY年MM月")
    }
    return ""
  }, [displayRangeType])

  const yearDataList = useMemo(() => {
    const filteredList = yearData.list.filter((v) => selectYearMemberIds.includes(v.member_id) && selectYearLabelGroupIds.includes(v.label_group_id))
    return _.range(1, 13).map((v) => {
      const prevYear = filteredList.filter((w) => w.year === year - 1 && w.month === v)
      const currentYear = filteredList.filter((w) => w.year === year && w.month === v)
      return ({
        label: `${v}月`,
        values: [
          {
            title: `${year - 1}年`,
            value: isDisplayTime ? prevYear.reduce((s, w) => s + w.time, 0) : prevYear.reduce((s, w) => s + w.cost, 0),
            color: "#CCCCCC",
          },
          {
            title: `${year}年`,
            value: isDisplayTime ? currentYear.reduce((s, w) => s + w.time, 0) : currentYear.reduce((s, w) => s + w.cost, 0),
            color: "#4472ba",
          },
        ],
        label_color: "#4472ba",
      })
    })
  }, [year, yearData.list, selectYearMemberIds, selectYearLabelGroupIds, isDisplayTime])

  const yearMax = useMemo(() => (
    Math.max(..._.range(1, 13).map((v) => {
      const prevYear = yearData.list.filter((w) => w.year === year - 1 && w.month === v)
      const currentYear = yearData.list.filter((w) => w.year === year && w.month === v)
      if (isDisplayTime) {
        return [prevYear.reduce((s, w) => s + w.time, 0), currentYear.reduce((s, w) => s + w.time, 0)]
      }
      return [prevYear.reduce((s, w) => s + w.cost, 0), currentYear.reduce((s, w) => s + w.cost, 0)]
    }).flat())
  ), [yearData.list, isDisplayTime])

  const sortDate = () => {
    if (!selectedLabelGroupCategory) return
    const [type, key, order] = sortLabel.split("@")
    if (!type || !key || !order) {
      setSelectedLabelGroupCategory(task.taskReportLabelGroupCategoryDetail?.label_group_category)
      return
    }
    if (order === "asc") {
      if (type === "label") {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          labels: selectedLabelGroupCategory.labels.map((v) => ({ ...v, members: sortBy(v.label_groups || [], key) })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          labels: sortBy(tmpSort.labels || [], key),
        })
      } else if (type === "label_code") {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          label_codes: selectedLabelGroupCategory.label_codes.map((v) => ({ ...v, labels: sortBy(v.labels || [], key) })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          label_codes: sortBy(tmpSort.label_codes || [], key),
        })
      } else {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          members: selectedLabelGroupCategory.members.map((v) => ({ ...v, labels: sortBy(v.label_groups || [], key) })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          members: sortBy(tmpSort.members || [], key),
        })
      }
    } else if (order === "desc") {
      if (type === "label") {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          labels: selectedLabelGroupCategory.labels.map((v) => ({ ...v, members: sortBy(v.label_groups || [], key).reverse() })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          labels: sortBy(tmpSort.labels || [], key).reverse(),
        })
      } else if (type === "label_code") {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          label_codes: selectedLabelGroupCategory.label_codes.map((v) => ({ ...v, labels: sortBy(v.labels || [], key).reverse() })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          label_codes: sortBy(tmpSort.label_codes || [], key).reverse(),
        })
      } else {
        const tmpSort = {
          ...selectedLabelGroupCategory,
          members: selectedLabelGroupCategory.members.map((v) => ({ ...v, labels: sortBy(v.label_groups || [], key).reverse() })),
        }
        setSelectedLabelGroupCategory({
          ...selectedLabelGroupCategory,
          members: sortBy(tmpSort.members || [], key).reverse(),
        })
      }
    }
  }

  const buildSortArea = (type: string, key: string) => {
    if (sortLabel === `${type}@${key}@asc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-numeric-down']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel(`${type}@${key}@desc`)}
          />
        </>
      )
    }
    if (sortLabel === `${type}@${key}@desc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-numeric-down-alt']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel("")}
          />
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon
          icon={['fas', 'sort']}
          style={{ cursor: "pointer" }}
          onClick={() => setSortLabel(`${type}@${key}@asc`)}
        />
      </>
    )
  }

  const clickGraph = (label: string) => {
    if (label === selectLabel) {
      setSelectLabel("")
      setDisplayRangeType("all")
      history.push(location.pathname)
      return
    }
    const tmpDate = format(label, "YYYY-MM-01")
    setDate(tmpDate)
    setDisplayRangeType("month")
    setSelectLabel(label)
    history.push(`${location.pathname}?date=${tmpDate}`)
  }

  const selectGraphDisplayRange = (prev = false) => {
    if (!task.taskReportLabelGroupCategoryDetailSummary || !task.taskReportLabelGroupCategoryDetailSummary.months.length) return
    const tmpStartDate = dateManipulateMonth(graphDisplayRange.startDate, prev ? -1 : 1, "YYYY-MM-DD")
    const tmpEndDate = dateManipulateMonth(graphDisplayRange.endDate, prev ? -1 : 1, "YYYY-MM-DD")
    setGraphDisplayRange({
      startDate: tmpStartDate,
      endDate: tmpEndDate,
    })
    const currentList = task.taskReportLabelGroupCategoryDetailSummary.months.map((v) => v.date)
    if (!currentList.includes(prev ? tmpStartDate : tmpEndDate)) {
      setIsGraphLoading(true)
      dispatch(
        fetchTaskReportLabelGroupCategoryDetailSummary(
          {
            label_group_category_id: id,
            start_date: prev ? tmpStartDate : tmpEndDate,
            end_date: prev ? tmpStartDate : tmpEndDate,
          },
        ),
      )
    } else {
      const rangeList = [...Array(DISPLAY_GRAPH_LIMIT)].map(
        (_v, i) => dateManipulateMonth(tmpStartDate, i, "YYYY-MM-DD"),
      )
      const dataList = task.taskReportLabelGroupCategoryDetailSummary?.months.filter((v) => rangeList.includes(v.date))
      setGraphData(dataList || [])
    }
  }

  const clickDetail = (memberId: number, labelId: number) => {
    if (login.function_enabled_state?.risk_assessment) {
      const tmpReqValue: RequestGetTaskSearch = {
        member_ids: [memberId.toString()],
        label_master_ids: [labelId.toString()],
        label_group_ids: [id],
      }
      if (displayRangeType === "month") {
        tmpReqValue.start_date = date
        tmpReqValue.end_date = endDate
      }
      dispatch(fetchTaskSearch(
        tmpReqValue,
      ))
    }
  }

  const buildCategoryRankingList = (children: {label_group_categories: LabelGroupCategory[], label_groups: LabelGroup[]}): LabelGroup[] => {
    if (!children.label_group_categories) return []
    return [...children.label_groups, ...children.label_group_categories.map((v) => buildCategoryRankingList(v.children))].flat()
  }

  const uniqueCategoryRankingList = (list: LabelGroup[]) => (
    Array.from(new Map(list.map((v) => [v.id, v])).values())
  )

  const clickSelectYearMemberId = (memberId: number) => {
    if (selectYearMemberIds.includes(memberId)) {
      setSelectYearMemberIds((cur) => cur.filter((v) => v !== memberId))
    } else {
      setSelectYearMemberIds((cur) => [...cur, memberId])
    }
  }

  const clickSelectYearLabelGroupId = (labelGroupId: number) => {
    if (selectYearLabelGroupIds.includes(labelGroupId)) {
      setSelectYearLabelGroupIds((cur) => cur.filter((v) => v !== labelGroupId))
    } else {
      setSelectYearLabelGroupIds((cur) => [...cur, labelGroupId])
    }
  }

  const clickSelectMonth = (month: number) => {
    setDate(`${year}-${`0${month}`.slice(-2)}-01`)
    setSelectLabel(`${year}-${`0${month}`.slice(-2)}-01`)
    setDisplayRangeType("month")
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setSelectedLabelGroupCategory(task.taskReportLabelGroupCategoryDetail?.label_group_category)
    setSortLabel("")
  }, [task.taskReportLabelGroupCategoryDetail])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date, "YYYY-MM-DD"))
      setSelectLabel(format(getParams.date, "YYYY/MM"))
      setDisplayRangeType("month")
      return
    }
    setDate(startOfMonth(nowDate(), 'YYYY-MM-DD'))
  }, [])

  useEffect(() => {
    if (displayRangeType === "all") {
      setSelectLabel("")
      history.push(location.pathname)
      return
    }
    history.push(`${location.pathname}?date=${date}`)
  }, [displayRangeType])

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false)
      return
    }
    if (displayRangeType === "all") {
      setSelectLabel("")
      dispatch(fetchTaskReportLabelGroupCategoryDetail(
        {
          id: id,
        },
      ))
      return
    }
    if (displayRangeType === "year") {
      setIsGraphLoading(true)
      dispatch(fetchTaskReportLabelGroupCategoryDetailYearlySummary(
        {
          label_group_category_id: id,
          start_date: `${year - 1}-01-01`,
          end_date: `${year}-12-01`,
        },
        (results) => {
          setYearData(results)
          setSelectYearMemberIds(results.members.map((v) => v.id))
          setSelectYearLabelGroupIds(results.label_groups.map((v) => v.id))
          setTimeout(() => {
            setIsGraphLoading(false)
          }, 500)
        },
      ))
      return
    }
    setSelectLabel(startOfMonth(date, 'YYYY-MM-DD'))
    if (!task.taskReportLabelGroupCategoryDetailSummary?.months.map((v) => v.date).includes(startOfMonth(date, 'YYYY-MM-DD'))) {
      dispatch(resetTaskReportLabelGroupCategoryDetailSummary())
    }
    dispatch(fetchTaskReportLabelGroupCategoryDetail(
      {
        id: id,
        start_date: startOfMonth(date, 'YYYY-MM-DD'),
        end_date: endDate,
      },
    ))
  }, [date, displayRangeType])

  useEffect(() => {
    if (displayRangeType !== "year") return
    const unSelectMemberIds = yearData.members.map((v) => v.id).filter((v) => !selectYearMemberIds.includes(v))
    const unSelectLabelGroupIds = yearData.label_groups.map((v) => v.id).filter((v) => !selectYearLabelGroupIds.includes(v))
    setIsGraphLoading(true)
    dispatch(fetchTaskReportLabelGroupCategoryDetailYearlySummary(
      {
        label_group_category_id: id,
        start_date: `${year - 1}-01-01`,
        end_date: `${year}-12-01`,
      },
      (results) => {
        setYearData(results)
        setDisplayMemberPage(1)
        setDisplayProjectPage(1)
        const tmpMemberIds = results.members.map((v) => v.id)
        if (unSelectMemberIds.length) {
          setSelectYearMemberIds(tmpMemberIds.filter((v) => !unSelectMemberIds.includes(v)))
        } else {
          setSelectYearMemberIds(tmpMemberIds)
        }
        const tmpLabelGroupIds = results.label_groups.map((v) => v.id)
        if (unSelectLabelGroupIds.length) {
          setSelectYearLabelGroupIds(tmpLabelGroupIds.filter((v) => !unSelectLabelGroupIds.includes(v)))
        } else {
          setSelectYearLabelGroupIds(tmpLabelGroupIds)
        }
        setTimeout(() => {
          setIsGraphLoading(false)
        }, 500)
      },
    ))
  }, [year])

  useEffect(() => {
    dispatch(fetchTaskReportLabelGroupCategoryDetailMax({ label_group_category_id: id }))
    dispatch(resetTaskReportLabelGroupCategoryDetailSummary())
  }, [])

  useEffect(() => {
    if (!task.taskReportLabelGroupCategoryDetail) return
    if (task.taskReportLabelGroupCategoryDetailSummary?.months.length) return
    // eslint-disable-next-line max-len
    const targetDate = format(date || nowDate(), "YYYY-MM-01")
    let tmpStartDate = ""
    let tmpEndDate = ""
    if (
      ((task.taskReportLabelGroupCategoryDetailSummary?.months.length || 0) < DISPLAY_GRAPH_LIMIT)
      || (Number(format(targetDate, "YYYYMM")) > Number(nowDate("YYYYMM")))
    ) {
      // フォーカスされている月が表示上限よりも未来
      tmpStartDate = dateManipulateMonth(date, -(DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-DD")
      tmpEndDate = format(date, "YYYY-MM-DD")
    } else if (Number(dateManipulateMonth(date, -(DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-DD")) > Number(format(targetDate, "YYYYMM"))) {
      // フォーカスされている月が表示上限よりも過去
      tmpStartDate = format(date, "YYYY-MM-DD")
      tmpEndDate = dateManipulateMonth(date, (DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-DD")
    } else if (DISPLAY_GRAPH_LIMIT % 2 === 0) {
      // フォーカスされている月が表示上限内にあり、表示上限数が偶数の場合
      tmpStartDate = dateManipulateMonth(targetDate, -((DISPLAY_GRAPH_LIMIT / 2) - 1), "YYYY-MM-01")
      tmpEndDate = dateManipulateMonth(targetDate, (DISPLAY_GRAPH_LIMIT / 2), "YYYY-MM-01")
    } else {
      // フォーカスされている月が表示上限内にあり、表示上限数が奇数の場合
      tmpStartDate = dateManipulateMonth(targetDate, -(Math.floor(DISPLAY_GRAPH_LIMIT / 2)), "YYYY-MM-01")
      tmpEndDate = dateManipulateMonth(targetDate, Math.floor(DISPLAY_GRAPH_LIMIT / 2), "YYYY-MM-01")
    }
    setIsGraphLoading(true)
    dispatch(
      fetchTaskReportLabelGroupCategoryDetailSummary(
        {
          label_group_category_id: id,
          start_date: tmpStartDate,
          end_date: tmpEndDate,
        },
      ),
    )
    setGraphDisplayRange({ startDate: tmpStartDate, endDate: tmpEndDate })
  }, [task.taskReportLabelGroupCategoryDetail])

  useEffect(() => {
    if (!task.taskReportLabelGroupCategoryDetailSummary?.months.length) return
    const rangeList = [...Array(DISPLAY_GRAPH_LIMIT)].map(
      (_v, i) => dateManipulateMonth(graphDisplayRange.startDate, i, "YYYY-MM-DD"),
    )
    const dataList = task.taskReportLabelGroupCategoryDetailSummary?.months.filter((v) => rangeList.includes(v.date))
    setGraphData(dataList || [])
    setTimeout(() => {
      setIsGraphLoading(false)
    }, 500)
  }, [task.taskReportLabelGroupCategoryDetailSummary])

  useEffect(() => {
    const buildTaskDetailList = task.taskSearch?.tasks.filter((v) => v.time >= (DISPLAY_TASK_DETAIL_LIMIT_MINUTES * 60)) || []
    const underLimitMinuteList = task.taskSearch?.tasks.filter((v) => v.time < (DISPLAY_TASK_DETAIL_LIMIT_MINUTES * 60)) || []
    if (underLimitMinuteList.length) {
      buildTaskDetailList.push({
        type: 'manual',
        service_name: "",
        icon: null,
        icon_color: null,
        name: `${DISPLAY_TASK_DETAIL_LIMIT_MINUTES}分以下の作業データが${underLimitMinuteList.length}件ありました`,
        file_path: null,
        time: underLimitMinuteList.reduce((s, v) => s + v.time, 0),
        cost: underLimitMinuteList.reduce((s, v) => s + v.cost, 0),
        label: null,
      })
    }
  }, [task.taskSearch?.tasks])

  useEffect(() => {
    sortDate()
  }, [sortLabel])

  useEffect(() => {
    if (task.isLoading) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [task])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - カテゴリーレポート - 詳細</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.TASK_CATEGORY_DETAIL.path, language)}
        >
          カテゴリーレポート
        </Link>
        &nbsp;&gt;&nbsp;
        {selectedLabelGroupCategory?.name}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {selectedLabelGroupCategory?.name}
        </h1>
        <div className="tools">
          <div
            style={{
              display: "flex",
              gap: "2px",
              borderRadius: "20px",
              backgroundColor: "#4472ba",
              color: "#FFFFFF",
              padding: "4px",
            }}
          >
            <button
              type="button"
              onClick={() => setDisplayRangeType("all")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 8px",
                backgroundColor: displayRangeType === "all" ? "#FFFFFF" : "#4472ba",
                color: displayRangeType === "all" ? "#4472ba" : "#FFFFFF",
                borderRadius: "20px",
              }}
            >
              <FontAwesomeIcon icon={['far', 'calendar-alt']} />
              <span
                style={{
                  fontSize: "10px",
                }}
              >
                全期間
              </span>
            </button>
            <button
              type="button"
              onClick={() => setDisplayRangeType("month")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 8px",
                backgroundColor: displayRangeType === "month" ? "#FFFFFF" : "#4472ba",
                color: displayRangeType === "month" ? "#4472ba" : "#FFFFFF",
                borderRadius: "20px",
              }}
            >
              <FontAwesomeIcon icon={['far', 'calendar-check']} />
              <span
                style={{
                  fontSize: "10px",
                }}
              >
                月間
              </span>
            </button>
            <button
              type="button"
              onClick={() => setDisplayRangeType("year")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 8px",
                backgroundColor: displayRangeType === "year" ? "#FFFFFF" : "#4472ba",
                color: displayRangeType === "year" ? "#4472ba" : "#FFFFFF",
                borderRadius: "20px",
              }}
            >
              <FontAwesomeIcon icon={['fas', 'chart-line']} />
              <span
                style={{
                  fontSize: "10px",
                }}
              >
                年間推移
              </span>
            </button>
          </div>
          <DateModule
            date={date}
            startMonth={format(date || nowDate(), "YYYYMM")}
            changeCallback={(callback_date) => {
              setDate(callback_date)
              history.push(`${location.pathname}?date=${callback_date}`)
            }}
            disabled={displayRangeType !== "month"}
            noLimit
          />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={
              `Qaseeカテゴリーレポート ${selectedLabelGroupCategory?.name} ${rangeTypeTitle}}`
            }
            target="pdf_area"
            beforeFunc={() => {
              setIsDisplayCategoryDetail(true)
              setIsDisplayMemberDetail(true)
              setIsDisplayLabelDetail(true)
            }}
            afterFunc={() => {
              setIsDisplayCategoryDetail(false)
              setIsDisplayMemberDetail(false)
              setIsDisplayLabelDetail(false)
            }}
            disabled={isDisablePdf}
          />
        </div>
      </section>
      {
        displayRangeType !== "year" && (
          <section className="page_display_button_area">
            <div>
              <button
                type="button"
                className={displayType === "all" ? "on" : ""}
                onClick={() => setDisplayType("all")}
              >
                <FontAwesomeIcon icon={['far', 'file-alt']} style={{ fontSize: "20px" }} />
                すべて表示
              </button>
            </div>
            <div>
              <button
                type="button"
                className={displayType === "graph" ? "on" : ""}
                onClick={() => setDisplayType("graph")}
              >
                <FontAwesomeIcon icon={['fas', 'chart-bar']} style={{ fontSize: "20px" }} />
                分析グラフ
              </button>
              <button
                type="button"
                className={displayType === "category" ? "on" : ""}
                onClick={() => setDisplayType("category")}
              >
                <FontAwesomeIcon icon={['fas', 'folder']} style={{ fontSize: "20px" }} />
                カテゴリー情報
              </button>
              <button
                type="button"
                className={displayType === "label" ? "on" : ""}
                onClick={() => setDisplayType("label")}
              >
                <FontAwesomeIcon icon={['fas', 'list']} style={{ fontSize: "20px" }} />
                業務別情報
              </button>
              <button
                type="button"
                className={displayType === "member" ? "on" : ""}
                onClick={() => setDisplayType("member")}
              >
                <FontAwesomeIcon icon={['fas', 'users']} style={{ fontSize: "20px" }} />
                ユーザー別情報
              </button>
              {
                login.role_permission?.task_project_cost_view && (
                  <button
                    type="button"
                    className={displayType === "cost" ? "on" : ""}
                    onClick={() => setDisplayType("cost")}
                  >
                    <FontAwesomeIcon icon={['fas', 'coins']} style={{ fontSize: "20px" }} />
                    損益計算
                  </button>
                )
              }
            </div>
          </section>
        )
      }
      <section id="pdf_area">
        {
          ["all", "graph"].includes(displayType) && (
            <>
              <section className="page_contents content-area task_detail">
                <div className="content-block">
                  <div className="block-header">
                    {
                      displayRangeType === "year" && (
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "#666666",
                          }}
                        >
                          年間推移
                        </div>
                      )
                    }
                  </div>
                  <div
                    className="block-detail"
                    style={{
                      position: "relative",
                    }}
                  >
                    <div className="tools" style={{ justifyContent: "space-between", marginBottom: "24px" }}>
                      {
                        login.role_permission?.task_project_cost_view && (
                          <>
                            {
                              isDisplayTime ? (
                                <div />
                              ) : (
                                <div className="cost_viewable_label">
                                  コスト表示権限を持つユーザーのみ表示されています
                                </div>
                              )
                            }
                            <button
                              type="button"
                              className={`display_switch ${isDisplayTime ? "check" : ""}`}
                              onClick={() => setIsDisplayTime(!isDisplayTime)}
                            >
                              <div>
                                <FontAwesomeIcon icon={['far', 'clock']} />
                                <span>
                                  業務実績時間を表示
                                </span>
                              </div>
                              <div>
                                <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                <span>
                                  コストを表示
                                </span>
                              </div>
                            </button>
                          </>
                        )
                      }
                    </div>
                    <>
                      {
                        displayRangeType === "year" ? (
                          <>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "16px",
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "12px 8px 8px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "8px",
                                    padding: "2px 4px",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  表示選択
                                </div>
                                {
                                  _.range(Number(format(commonPart.company?.created_at, "YYYY")), Number(nowDate("YYYY")) + 1).map((v) => (
                                    <button
                                      type="button"
                                      onClick={() => setYear(v)}
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        padding: "4px 8px",
                                        backgroundColor: year === v ? "#f0fff0" : "#FFFFFF",
                                        color: year === v ? "#248a23" : "#666666",
                                      }}
                                    >
                                      {`${v}年`}
                                    </button>
                                  ))
                                }
                              </div>
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "12px 8px 8px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "8px",
                                    padding: "2px 4px",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  ユーザー
                                </div>
                                {
                                  selectYearMemberIds.length < yearData.members.length ? (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSelectYearMemberIds(yearData.members.map((v) => v.id) || [])}
                                    >
                                      すべて選択する
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSelectYearMemberIds([])}
                                    >
                                      すべて選択解除する
                                    </button>
                                  )
                                }
                                {
                                  yearData.members.slice(0, displayMemberPage * DISPLAY_MEMBER_LIMIT).map((v) => (
                                    <button
                                      type="button"
                                      onClick={() => clickSelectYearMemberId(v.id)}
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "20px",
                                        padding: "2px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontSize: "10px",
                                        cursor: "pointer",
                                        backgroundColor: selectYearMemberIds.includes(v.id) ? "#f0fff0" : "#EEEEEE",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={["fas", selectYearMemberIds.includes(v.id) ? "check" : "ban"]}
                                        fixedWidth
                                        style={{
                                          color: selectYearMemberIds.includes(v.id) ? "#248a23" : "#666666",
                                        }}
                                      />
                                      {
                                        v.profile_image ? (
                                          <img
                                            alt="profile"
                                            src={v.profile_image}
                                            className="profile_icon"
                                            style={{
                                              borderRadius: "50%",
                                              width: "10px",
                                            }}
                                          />
                                        ) : (
                                          <img
                                            alt=""
                                            src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                            className="profile_icon"
                                            style={{
                                              borderRadius: "50%",
                                              width: "10px",
                                            }}
                                          />
                                        )
                                      }
                                      {v.name}
                                    </button>
                                  ))
                                }
                                {
                                  displayMemberPage * DISPLAY_MEMBER_LIMIT < yearData.members.length && (
                                    <button
                                      type="button"
                                      onClick={() => setDisplayMemberPage((cur) => cur + 1)}
                                      style={{
                                        width: "100%",
                                        textAlign: "center",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                      }}
                                    >
                                      もっと見る
                                    </button>
                                  )
                                }
                              </div>
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "12px 8px 8px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "8px",
                                    padding: "2px 4px",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  プロジェクト
                                </div>
                                {
                                  selectYearLabelGroupIds.length !== yearData.label_groups.length ? (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSelectYearLabelGroupIds(yearData.label_groups.map((v) => v.id) || [])}
                                    >
                                      すべて選択する
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSelectYearLabelGroupIds([])}
                                    >
                                      すべて選択解除する
                                    </button>
                                  )
                                }
                                {
                                  yearData.label_groups.slice(0, displayProjectPage * DISPLAY_PROJECT_LIMIT).map((v) => (
                                    <button
                                      type="button"
                                      onClick={() => clickSelectYearLabelGroupId(v.id)}
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "20px",
                                        padding: "2px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontSize: "10px",
                                        cursor: "pointer",
                                        backgroundColor: selectYearLabelGroupIds.includes(v.id) ? "#f0fff0" : "#EEEEEE",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={["fas", selectYearLabelGroupIds.includes(v.id) ? "check" : "ban"]}
                                        fixedWidth
                                        style={{
                                          color: selectYearLabelGroupIds.includes(v.id) ? "#248a23" : "#666666",
                                        }}
                                      />
                                      <div
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                          backgroundColor: v.color || "#CCCCCC",
                                        }}
                                      />
                                      {v.name}
                                    </button>
                                  ))
                                }
                                {
                                  displayProjectPage * DISPLAY_PROJECT_LIMIT < yearData.label_groups.length && (
                                    <button
                                      type="button"
                                      onClick={() => setDisplayProjectPage((cur) => cur + 1)}
                                      style={{
                                        width: "100%",
                                        textAlign: "center",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                      }}
                                    >
                                      もっと見る
                                    </button>
                                  )
                                }
                              </div>
                            </div>
                            <LineChart
                              list={yearDataList}
                              chartSize={250}
                              type={isDisplayTime ? "hour" : "yen"}
                              ruler
                              scale
                              vLabelLimit={10}
                              gap
                              max={yearMax}
                              hLabelCallback={(month) => clickSelectMonth(parseInt(month, 10))}
                              isGraphLoading={isGraphLoading}
                            />
                          </>
                        ) : (
                          <>
                            {
                              isDisplayTime ? (
                                <DetailScheduleChart
                                  list={graphData.map((v) => (
                                    {
                                      label: v.date,
                                      scheduled: v.labeling.scheduled,
                                      values: v.labeling.label_groups.map((w) => (
                                        {
                                          name: w.name,
                                          value: w.time,
                                          color: w.color,
                                        }
                                      )),
                                      color: "#CCCCCC",
                                    }
                                  )) || []}
                                  chartSize={300}
                                  ruler
                                  scale
                                  type="hour"
                                  vLabelLimit={5}
                                  legend
                                  tips
                                  clickCallback={(label) => clickGraph(label)}
                                  selectLabel={selectLabel}
                                  max={task.taskReportLabelGroupCategoryDetailMax?.max_time || 0}
                                  isGraphLoading={isGraphLoading}
                                />
                              ) : (
                                <DetailCostChart
                                  list={graphData.map((v) => (
                                    {
                                      label: v.date,
                                      sales: v.accounting.budget || 0,
                                      member_cost: v.accounting.cost || 0,
                                      other_cost: v.accounting.other_costs.reduce((s, w) => s + w.cost, 0) || 0,
                                    }
                                  )) || []}
                                  chartSize={300}
                                  ruler
                                  scale
                                  type="yen"
                                  vLabelLimit={5}
                                  tips
                                  clickCallback={(label) => clickGraph(label)}
                                  selectLabel={selectLabel}
                                  max={task.taskReportLabelGroupCategoryDetailMax?.max_cost || 0}
                                  isGraphLoading={isGraphLoading}
                                />
                              )
                            }
                            <div>
                              <button
                                type="button"
                                onClick={() => selectGraphDisplayRange(true)}
                                style={{
                                  position: "absolute",
                                  bottom: "30%",
                                  left: "100px",
                                  transform: "translateY(5%)",
                                  zIndex: "1",
                                  fontSize: "20px",
                                  color: "#999999",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                              </button>
                              <button
                                type="button"
                                onClick={() => selectGraphDisplayRange()}
                                style={{
                                  position: "absolute",
                                  bottom: "30%",
                                  right: "-5px",
                                  transform: "translateY(5%)",
                                  zIndex: "1",
                                  fontSize: "20px",
                                  color: "#999999",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                              </button>
                            </div>
                          </>
                        )
                      }
                    </>
                  </div>
                </div>
              </section>
            </>
          )
        }
        {
          displayRangeType !== "year" && ["all", "label", "member", "cost", "category"].includes(displayType) && (
            <>
              <section
                className="page_contents content-area task_detail"
                style={{
                  border: "solid 3px #4472ba",
                  borderRadius: "8px",
                }}
              >
                <div className="content-block">
                  {
                    task.isLoading ? (
                      <LoadingSquare>
                        <>
                          カテゴリーレポートを生成しています…
                        </>
                      </LoadingSquare>
                    ) : (
                      <>
                        {
                          selectedLabelGroupCategory ? (
                            <>
                              <div
                                style={{
                                  width: "calc(100% + 96px)",
                                  backgroundColor: "#4472ba",
                                  color: "#FFFFFF",
                                  fontSize: "18px",
                                  margin: "-48px -16px 40px -48px",
                                  padding: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                  fontWeight: "bold",
                                }}
                              >
                                表示データ：
                                <div
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    color: "#4572ba",
                                    borderRadius: "4px",
                                    padding: "8px 24px",
                                    fontSize: "24px",
                                    display: "flex",
                                    alignItems: "flex-end",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    displayRangeType === "all" ? (
                                      <>
                                        <div>
                                          全ての期間
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "18px",
                                              marginRight: "4px",
                                            }}
                                          >
                                            {format(date, "YYYY年")}
                                          </span>
                                          {startOfMonth(date, "MM月DD日")}
                                        </div>
                                        ~
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "18px",
                                              marginRight: "4px",
                                            }}
                                          >
                                            {format(endDate, "YYYY年")}
                                          </span>
                                          {format(endDate, "MM月DD日")}
                                        </div>
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                              <div className="block-detail">
                                {
                                  !!task.taskReportLabelGroupDetail?.threshold_time && (
                                    <div
                                      style={{
                                        textAlign: "right",
                                        marginBottom: "16px",
                                      }}
                                    >
                                      {`${format(task.taskReportLabelGroupDetail?.threshold_time, "YYYY年M月D日HH時mm分ss秒")}時点の集計データを表示しています`}
                                    </div>
                                  )
                                }
                                {
                                  ["all", "category"].includes(displayType) && (
                                    <div className="box_area">
                                      <div className="title_area">
                                        <div className="title" style={{ justifyContent: "space-between", width: "100%" }}>
                                          カテゴリー情報
                                          <div className="tools" style={{ justifyContent: "space-between" }}>
                                            {
                                              login.role_permission?.task_project_cost_view && (
                                                <>
                                                  {
                                                    isDisplayCategoryTime ? (
                                                      <div />
                                                    ) : (
                                                      <div className="cost_viewable_label">
                                                        コスト表示権限を持つユーザーのみ表示されています
                                                      </div>
                                                    )
                                                  }
                                                  <button
                                                    type="button"
                                                    className={`display_switch ${isDisplayCategoryTime ? "check" : ""}`}
                                                    onClick={() => setIsDisplayCategoryTime(!isDisplayCategoryTime)}
                                                  >
                                                    <div>
                                                      <FontAwesomeIcon icon={['far', 'clock']} />
                                                      <span>
                                                        実績時間
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                                      <span>
                                                        実績コスト
                                                      </span>
                                                    </div>
                                                  </button>
                                                </>
                                              )
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <ReportSummaryModule
                                        list={uniqueCategoryRankingList(buildCategoryRankingList(selectedLabelGroupCategory.children)).map((v) => ({
                                          id: v.id,
                                          name: v.name,
                                          color: v.color,
                                          cost: v.cost.actual,
                                          time: v.time.actual,
                                        }))}
                                        type="label"
                                        graphTitle="カテゴリー情報"
                                        headerTitle={`プロジェクト(実績${isDisplayCategoryTime ? "時間" : "コスト"}が多い順)`}
                                        chartSize={300}
                                        valueType={isDisplayCategoryTime ? "time" : "cost"}
                                      />
                                      {
                                        isDisplayCategoryDetail ? (
                                          <>
                                            {
                                              // eslint-disable-next-line max-len
                                              (selectedLabelGroupCategory.children.label_group_categories.length || selectedLabelGroupCategory.children.label_groups.length) ? (
                                                <>
                                                  <div className="list_row_wrapper">
                                                    {/* eslint-disable-next-line max-len */}
                                                    <div className={`list_row header_area${login.role_permission?.task_project_cost_view ? " cost_view" : ""}`}>
                                                      <div className="title_display_area">
                                                        カテゴリー名 / プロジェクト名
                                                      </div>
                                                      <div className="work_time">
                                                        業務時間
                                                      </div>
                                                      {
                                                        login.role_permission?.task_project_cost_view && (
                                                          <div className="work_cost">
                                                            業務コスト
                                                            <Tips
                                                              description="コスト表示権限を持つユーザーのみ表示されています"
                                                              className="fit"
                                                              icon={false}
                                                            >
                                                              <>
                                                                <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                              </>
                                                            </Tips>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <DetailRowForCategory
                                                      list={selectedLabelGroupCategory.children}
                                                      costView={login.role_permission?.task_project_cost_view || false}
                                                      displayApprovalIcon={Number(format(date, "YYYYMMDD")) > 20240430}
                                                    />
                                                  </div>
                                                </>
                                              ) : (
                                                <div
                                                  style={{
                                                    marginBottom: "24px",
                                                    fontSize: "14px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  データがありません
                                                </div>
                                              )
                                            }
                                          </>
                                        ) : (
                                          <></>
                                        )
                                      }
                                      <div
                                        onClick={() => setIsDisplayCategoryDetail(!isDisplayCategoryDetail)}
                                        role="none"
                                        style={{
                                          marginTop: "24px",
                                          textAlign: "center",
                                          backgroundColor: "#4572ba",
                                          color: "#FFFFFF",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          cursor: "pointer",
                                          fontWeight: "bold",
                                          fontSize: "16px",
                                        }}
                                      >
                                        {isDisplayLabelDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                      </div>
                                    </div>
                                  )
                                }
                                {
                                  ["all", "label"].includes(displayType) && (
                                    <>
                                      <div className="box_area">
                                        <div className="title_area">
                                          <div className="title" style={{ justifyContent: "space-between", width: "100%" }}>
                                            業務別プロジェクト情報
                                            <div className="tools" style={{ justifyContent: "space-between" }}>
                                              {
                                                login.role_permission?.task_project_cost_view && (
                                                  <>
                                                    {
                                                      isDisplayWorkTime ? (
                                                        <div />
                                                      ) : (
                                                        <div className="cost_viewable_label">
                                                          コスト表示権限を持つユーザーのみ表示されています
                                                        </div>
                                                      )
                                                    }
                                                    <button
                                                      type="button"
                                                      className={`display_switch ${isDisplayWorkTime ? "check" : ""}`}
                                                      onClick={() => setIsDisplayWorkTime(!isDisplayWorkTime)}
                                                    >
                                                      <div>
                                                        <FontAwesomeIcon icon={['far', 'clock']} />
                                                        <span>
                                                          実績時間
                                                        </span>
                                                      </div>
                                                      <div>
                                                        <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                                        <span>
                                                          実績コスト
                                                        </span>
                                                      </div>
                                                    </button>
                                                  </>
                                                )
                                              }
                                            </div>
                                          </div>
                                        </div>
                                        <ReportSummaryModule
                                          list={selectedLabelGroupCategory.labels || []}
                                          type="label"
                                          graphTitle="業務別プロジェクト情報"
                                          headerTitle={`業務(実績${isDisplayWorkTime ? "時間" : "コスト"}が多い順)`}
                                          chartSize={300}
                                          valueType={isDisplayWorkTime ? "time" : "cost"}
                                        />
                                        {
                                          isDisplayLabelDetail ? (
                                            <>
                                              {
                                                selectedLabelGroupCategory.members.length ? (
                                                  <>
                                                    <div className="list_row_wrapper">
                                                      {/* eslint-disable-next-line max-len */}
                                                      <div className={`list_row header_area${login.role_permission?.task_project_cost_view ? " cost_view" : ""}`}>
                                                        <div className="title_display_area">
                                                          業務名
                                                        </div>
                                                        <div className="work_time">
                                                          業務時間
                                                          {buildSortArea("label", "time")}
                                                        </div>
                                                        {
                                                          login.role_permission?.task_project_cost_view && (
                                                            <div className="work_cost">
                                                              業務コスト
                                                              {buildSortArea("label", "cost")}
                                                              <Tips
                                                                description="コスト表示権限を持つユーザーのみ表示されています"
                                                                className="fit"
                                                                icon={false}
                                                              >
                                                                <>
                                                                  <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                                </>
                                                              </Tips>
                                                            </div>
                                                          )
                                                        }
                                                      </div>
                                                    </div>
                                                    <div>
                                                      {
                                                        selectedLabelGroupCategory.labels.map((label) => (
                                                          <DetailRow
                                                            key={`detail_row_${id}_${label.name}_${label.id}`}
                                                            list={{
                                                              ...label,
                                                              details: label.label_groups,
                                                            }}
                                                            type="label"
                                                            costView={login.role_permission?.task_project_cost_view || false}
                                                            clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                            displayApprovalIcon={Number(format(date, "YYYYMMDD")) > 20240430}
                                                            isProject
                                                          />
                                                        ))
                                                      }
                                                    </div>
                                                  </>
                                                ) : (
                                                  <div
                                                    style={{
                                                      marginBottom: "24px",
                                                      fontSize: "14px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    データがありません
                                                  </div>
                                                )
                                              }
                                            </>
                                          ) : (
                                            <></>
                                          )
                                        }
                                        <div
                                          onClick={() => setIsDisplayLabelDetail(!isDisplayLabelDetail)}
                                          role="none"
                                          style={{
                                            marginTop: "24px",
                                            textAlign: "center",
                                            backgroundColor: "#4572ba",
                                            color: "#FFFFFF",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            fontSize: "16px",
                                          }}
                                        >
                                          {isDisplayLabelDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                        </div>
                                      </div>
                                      {
                                        !!selectedLabelGroupCategory.label_codes.length && (
                                          <div className="box_area">
                                            <div className="title_area">
                                              <div className="title" style={{ justifyContent: "space-between", width: "100%" }}>
                                                業務コード別業務情報
                                                <div className="tools" style={{ justifyContent: "space-between" }}>
                                                  {
                                                    login.role_permission?.task_project_cost_view && (
                                                      <>
                                                        {
                                                          isDisplayWorkCodeTime ? (
                                                            <div />
                                                          ) : (
                                                            <div className="cost_viewable_label">
                                                              コスト表示権限を持つユーザーのみ表示されています
                                                            </div>
                                                          )
                                                        }
                                                        <button
                                                          type="button"
                                                          className={`display_switch ${isDisplayWorkCodeTime ? "check" : ""}`}
                                                          onClick={() => setIsDisplayWorkCodeTime(!isDisplayWorkCodeTime)}
                                                        >
                                                          <div>
                                                            <FontAwesomeIcon icon={['far', 'clock']} />
                                                            <span>
                                                              実績時間
                                                            </span>
                                                          </div>
                                                          <div>
                                                            <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                                            <span>
                                                              実績コスト
                                                            </span>
                                                          </div>
                                                        </button>
                                                      </>
                                                    )
                                                  }
                                                </div>
                                              </div>
                                            </div>
                                            <ReportSummaryModule
                                              list={
                                                selectedLabelGroupCategory.label_codes?.map((v, i) => ({
                                                  id: i + 1,
                                                  name: v.code || "業務コードなし",
                                                  time: v.time,
                                                  cost: v.cost,
                                                  color: v.code ? stringToHexSimple(v.code) : "#CCCCCC",
                                                })) || []
                                              }
                                              type="label"
                                              graphTitle="業務コード別業務情報"
                                              headerTitle={`業務コード(実績${isDisplayWorkCodeTime ? "時間" : "コスト"}が多い順)`}
                                              chartSize={300}
                                              valueType={isDisplayWorkCodeTime ? "time" : "cost"}
                                            />
                                            {
                                              isDisplayLabelCodeGroup ? (
                                                <>
                                                  {
                                                    selectedLabelGroupCategory.members.length ? (
                                                      <>
                                                        <div className="list_row_wrapper">
                                                          { /* eslint-disable-next-line max-len */ }
                                                          <div className={`list_row header_area${login.role_permission?.task_project_cost_view ? " cost_view" : ""}`}>
                                                            <div className="title_display_area">
                                                              業務コード名
                                                            </div>
                                                            <div className="work_time">
                                                              業務時間
                                                              {buildSortArea("label_code", "time")}
                                                            </div>
                                                            {
                                                              login.role_permission?.task_project_cost_view && (
                                                                <div className="work_cost">
                                                                  業務コスト
                                                                  {buildSortArea("label_code", "cost")}
                                                                  <Tips
                                                                    description="コスト表示権限を持つユーザーのみ表示されています"
                                                                    className="fit"
                                                                    icon={false}
                                                                  >
                                                                    <>
                                                                      <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                                    </>
                                                                  </Tips>
                                                                </div>
                                                              )
                                                            }
                                                          </div>
                                                        </div>
                                                        <div>
                                                          {
                                                            selectedLabelGroupCategory.label_codes.map((label_code, i) => (
                                                              <DetailRow
                                                                key={`detail_row_${id}_${label_code.code}`}
                                                                list={{
                                                                  ...label_code,
                                                                  id: i + 1,
                                                                  name: label_code.code || "業務コードなし",
                                                                  color: label_code.code ? stringToHexSimple(label_code.code) : "#CCCCCC",
                                                                  details: label_code.labels,
                                                                }}
                                                                type="label_code"
                                                                costView={login.role_permission?.task_project_cost_view || false}
                                                                clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                                displayApprovalIcon={Number(format(date, "YYYYMMDD")) > 20240430}
                                                                round
                                                              />
                                                            ))
                                                          }
                                                        </div>
                                                      </>
                                                    ) : (
                                                      <div
                                                        style={{
                                                          marginBottom: "24px",
                                                          fontSize: "14px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        データがありません
                                                      </div>
                                                    )
                                                  }
                                                </>
                                              ) : (
                                                <></>
                                              )
                                            }
                                            <div
                                              onClick={() => setIsDisplayLabelCodeGroup(!isDisplayLabelCodeGroup)}
                                              role="none"
                                              style={{
                                                marginTop: "24px",
                                                textAlign: "center",
                                                backgroundColor: "#4572ba",
                                                color: "#FFFFFF",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                fontSize: "16px",
                                              }}
                                            >
                                              {isDisplayLabelCodeGroup ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                            </div>
                                          </div>
                                        )
                                      }
                                    </>
                                  )
                                }
                                {
                                  ["all", "member"].includes(displayType) && (
                                    <div className="box_area">
                                      <div className="title_area">
                                        <div className="title" style={{ justifyContent: "space-between", width: "100%" }}>
                                          ユーザー別プロジェクト情報
                                          <div className="tools" style={{ justifyContent: "space-between" }}>
                                            {
                                              login.role_permission?.task_project_cost_view && (
                                                <>
                                                  {
                                                    isDisplayMemberTime ? (
                                                      <div />
                                                    ) : (
                                                      <div className="cost_viewable_label">
                                                        コスト表示権限を持つユーザーのみ表示されています
                                                      </div>
                                                    )
                                                  }
                                                  <button
                                                    type="button"
                                                    className={`display_switch ${isDisplayMemberTime ? "check" : ""}`}
                                                    onClick={() => setIsDisplayMemberTime(!isDisplayMemberTime)}
                                                  >
                                                    <div>
                                                      <FontAwesomeIcon icon={['far', 'clock']} />
                                                      <span>
                                                        実績時間
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                                      <span>
                                                        実績コスト
                                                      </span>
                                                    </div>
                                                  </button>
                                                </>
                                              )
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <ReportSummaryModule
                                        list={selectedLabelGroupCategory.members}
                                        type="member"
                                        graphTitle="ユーザー別プロジェクト情報"
                                        headerTitle={`ユーザー(実績${isDisplayMemberTime ? "時間" : "コスト"}が多い順)`}
                                        chartSize={300}
                                        valueType={isDisplayMemberTime ? "time" : "cost"}
                                      />
                                      {
                                        isDisplayMemberDetail ? (
                                          <>
                                            {
                                              selectedLabelGroupCategory.members.length ? (
                                                <>
                                                  <div className="list_row_wrapper">
                                                    {/* eslint-disable-next-line max-len */}
                                                    <div className={`list_row header_area${login.role_permission?.task_project_cost_view ? " cost_view" : ""}`}>
                                                      <div className="title_display_area">
                                                        ユーザー名
                                                      </div>
                                                      <div className="work_time">
                                                        業務時間
                                                        {buildSortArea("member", "time")}
                                                      </div>
                                                      {
                                                        login.role_permission?.task_project_cost_view && (
                                                          <div className="work_cost">
                                                            業務コスト
                                                            {buildSortArea("member", "cost")}
                                                            <Tips
                                                              description="コスト表示権限を持つユーザーのみ表示されています"
                                                              className="fit"
                                                              icon={false}
                                                            >
                                                              <>
                                                                <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                              </>
                                                            </Tips>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  <div>
                                                    {
                                                      selectedLabelGroupCategory.members.map((member) => (
                                                        <DetailRow
                                                          key={`detail_row_${id}_${member.name}_${member.id}`}
                                                          list={{
                                                            ...member,
                                                            details: member.label_groups,
                                                          }}
                                                          type="member"
                                                          costView={login.role_permission?.task_project_cost_view || false}
                                                          clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                          displayApprovalIcon={Number(format(date, "YYYYMMDD")) > 20240430}
                                                        />
                                                      ))
                                                    }
                                                  </div>
                                                </>
                                              ) : (
                                                <div
                                                  style={{
                                                    marginBottom: "24px",
                                                    fontSize: "14px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  データがありません
                                                </div>
                                              )
                                            }
                                          </>
                                        ) : (
                                          <></>
                                        )
                                      }
                                      <div
                                        onClick={() => setIsDisplayMemberDetail(!isDisplayMemberDetail)}
                                        role="none"
                                        style={{
                                          marginTop: "24px",
                                          textAlign: "center",
                                          backgroundColor: "#4572ba",
                                          color: "#FFFFFF",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          cursor: "pointer",
                                          fontWeight: "bold",
                                          fontSize: "16px",
                                        }}
                                      >
                                        {isDisplayMemberDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                      </div>
                                    </div>
                                  )
                                }
                                {
                                  login.role_permission?.task_project_cost_view && ["all", "cost"].includes(displayType) ? (
                                    <div className="box_area">
                                      <div className="title_area">
                                        <div className="title">
                                          損益計算
                                          <span className="cost_view">
                                            コスト表示権限を持つユーザーのみ表示されています
                                          </span>
                                        </div>
                                      </div>
                                      <div className="profit_area">
                                        <div className="breakdown_list">
                                          <div className="breakdown_row budget">
                                            <div>
                                              売上・予算
                                            </div>
                                            <div>
                                              {`¥${Math.floor(selectedLabelGroupCategory.cost.scheduled || 0).toLocaleString() || "0"}`}
                                            </div>
                                          </div>
                                          <div className="breakdown_row work_cost">
                                            <div>
                                              業務コスト
                                            </div>
                                            <div>
                                              {`¥${Math.floor(selectedLabelGroupCategory.cost.actual).toLocaleString() || "0"}`}
                                            </div>
                                          </div>
                                          <div>
                                            {
                                              selectedLabelGroupCategory.labels.map((label) => (
                                                <div
                                                  key={uniqueId()}
                                                  className="breakdown_row work_cost_detail"
                                                >
                                                  <div>
                                                    <div
                                                      className="icon"
                                                      style={{
                                                        backgroundColor: label.color,
                                                      }}
                                                    />
                                                    {label.name}
                                                  </div>
                                                  <div>
                                                    {`¥${label.cost.toLocaleString()}`}
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                          <div className="breakdown_row other_cost">
                                            <div>
                                              その他のコスト
                                            </div>
                                            <div>
                                              {`¥${selectedLabelGroupCategory.cost.others.reduce((s, v) => s + v.cost, 0).toLocaleString()}`}
                                            </div>
                                          </div>
                                          <div>
                                            {
                                              selectedLabelGroupCategory.cost.others.map((other) => (
                                                <div
                                                  key={uniqueId()}
                                                  className="breakdown_row other_cost_detail"
                                                >
                                                  <div>
                                                    {other.name}
                                                  </div>
                                                  <div>
                                                    {`¥${other.cost.toLocaleString()}`}
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                          <div className={`breakdown_row result${profit <= 0 ? " negative" : ""}`}>
                                            <div>
                                              {profit <= 0 ? "損失" : "利益"}
                                            </div>
                                            <div>
                                              {`¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="graph_wrapper">
                                          <div className="graph_area">
                                            <div>
                                              {
                                                selectedLabelGroupCategory.labels.map((label) => (
                                                  <React.Fragment key={uniqueId()}>
                                                    {
                                                      label.cost ? (
                                                        <div
                                                          className="block work"
                                                          style={{
                                                            height: `${(label.cost / graphLimit) * 100}%`,
                                                          }}
                                                        >
                                                          {
                                                            ((label.cost / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                              <div className="text_area">
                                                                <div>
                                                                  <div
                                                                    className="icon"
                                                                    style={{
                                                                      backgroundColor: label.color,
                                                                    }}
                                                                  />
                                                                  {label.name}
                                                                </div>
                                                                <div>
                                                                  {`¥${label.cost.toLocaleString()}`}
                                                                </div>
                                                              </div>
                                                            ) : (
                                                              <Tips
                                                                description={`${label.name}:¥${label.cost.toLocaleString()}`}
                                                                className="min"
                                                                icon={false}
                                                              >
                                                                <>
                                                                </>
                                                              </Tips>
                                                            )
                                                          }
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                  </React.Fragment>
                                                ))
                                              }
                                              {
                                                selectedLabelGroupCategory.cost.others.map((other) => (
                                                  <React.Fragment key={uniqueId()}>
                                                    <div
                                                      className="block other"
                                                      style={{
                                                        height: `${(other.cost / graphLimit) * 100}%`,
                                                      }}
                                                    >
                                                      {
                                                        ((other.cost / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                          <div className="text_area">
                                                            <div>
                                                              {other.name}
                                                            </div>
                                                            <div>
                                                              {`¥${Math.floor(other.cost).toLocaleString()}`}
                                                            </div>
                                                          </div>
                                                        ) : (
                                                          <Tips
                                                            description={`${other.name}:¥${Math.floor(other.cost).toLocaleString()}`}
                                                            className="min"
                                                            icon={false}
                                                          >
                                                            <>
                                                            </>
                                                          </Tips>
                                                        )
                                                      }
                                                    </div>
                                                  </React.Fragment>
                                                ))
                                              }
                                              {
                                                profit > 0 && (
                                                  <div
                                                    className="block result"
                                                    style={{
                                                      height: `${(profit / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((profit / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            利益
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(profit).toLocaleString()}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          description={`利益:¥${Math.floor(profit).toLocaleString()}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                )
                                              }
                                            </div>
                                            <div>
                                              {
                                                selectedLabelGroupCategory.cost.scheduled && selectedLabelGroupCategory.cost.scheduled > 0 ? (
                                                  <div
                                                    className="block budget"
                                                    style={{
                                                      height: `${(selectedLabelGroupCategory.cost.scheduled / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((selectedLabelGroupCategory.cost.scheduled / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            売上・予算
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(selectedLabelGroupCategory.cost.scheduled || 0).toLocaleString() || "0"}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          // eslint-disable-next-line max-len
                                                          description={`売上・予算:¥${Math.floor(selectedLabelGroupCategory.cost.scheduled || 0).toLocaleString() || "0"}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                profit <= 0 && (
                                                  <div
                                                    className="block result negative"
                                                    style={{
                                                      height: `${(Math.abs(profit) / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((Math.abs(profit) / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            損失
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          description={`損失:¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                )
                                              }
                                            </div>
                                          </div>
                                          <div className="label_area">
                                            <div>
                                              コストと利益
                                            </div>
                                            <div>
                                              収益
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <></>
                                  )
                                }
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="block-detail">
                                <div
                                  style={{
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: "24px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    該当するプロジェクトがありませんでした
                                  </div>
                                </div>
                              </div>
                            </>
                          )
                        }
                      </>
                    )
                  }
                </div>
              </section>
            </>
          )
        }
      </section>
    </section>
  )
}

export default Detail
